// Spreading co-located markers apart on screen.
//
// A gazetteer answers "Tehran" with ONE coordinate, so every situation that
// resolves to the city name lands on the identical point and the map draws
// one dot with a count badge hiding four others under it. Grouping does not
// help — those are genuinely different situations, hours or days apart — so
// the fix is presentation: markers that would draw on top of each other are
// fanned out around the shared point, each on a short leg back to where it
// really is. The same treatment covers near-identical points (two geocodes a
// few hundred metres apart at theatre zoom), because "stacked" is a question
// about PIXELS, not degrees.
//
// Pure, and in screen space: the caller says what zoom the map is at and gets
// back offsets in degrees that amount to a fixed pixel fan at that zoom. The
// circle layers that draw the dots have no data-driven pixel translate, so a
// geographic offset re-baked on zoom is the only way to keep the fan the same
// size on screen — at a different zoom the same points may not stack at all,
// and then they draw where they are.

export interface SpreadInput {
  lon: number;
  lat: number;
  /** Sort key within a fan — members are laid out in ascending order, so a
   *  newcomer takes the next slot instead of reshuffling the others. */
  order?: number;
}

export interface SpreadResult {
  lon: number;
  lat: number;
  /** The point this marker was moved away from; absent when it was not moved. */
  from?: [number, number];
}

/** Two markers closer than this on screen count as stacked. The dot body is
 *  ~9–12 px across and its glyph sits above it, so 14 px is "overlapping". */
export const STACK_PX = 14;
/** Spacing between neighbours on the fan, and the smallest fan radius. */
const SPACING_PX = 16;
const MIN_RADIUS_PX = 12;
/** Up to this many members sit on one ring; beyond it the fan spirals so a
 *  thirty-deep stack still reads as thirty markers, not a wider ring. */
const RING_MAX = 8;
/** MapLibre's world is 512 px wide at zoom 0. */
const WORLD_PX_Z0 = 512;

function worldPx(zoom: number): number {
  return WORLD_PX_Z0 * 2 ** zoom;
}

/** Web-Mercator pixel coordinates at a zoom — the space stacking lives in. */
function toPx(lon: number, lat: number, world: number): [number, number] {
  const clamped = Math.max(-85.05, Math.min(85.05, lat));
  const phi = (clamped * Math.PI) / 180;
  const x = ((lon + 180) / 360) * world;
  const y = (0.5 - Math.log(Math.tan(Math.PI / 4 + phi / 2)) / (2 * Math.PI)) * world;
  return [x, y];
}

/** Pixel offsets for a fan of `n` markers: a ring when few, a spiral when
 *  many. Slot 0 sits at 12 o'clock and slots run clockwise. Exported so the
 *  test can pin the geometry without going through the projection. */
export function fanOffsets(n: number): [number, number][] {
  const out: [number, number][] = [];
  if (n <= RING_MAX) {
    const r = Math.max(MIN_RADIUS_PX, (SPACING_PX * n) / (2 * Math.PI));
    for (let i = 0; i < n; i++) {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      out.push([r * Math.cos(a), r * Math.sin(a)]);
    }
    return out;
  }
  // Archimedean spiral: radius grows by one spacing per turn, and the angular
  // step shrinks with radius so neighbours along the arc stay one spacing
  // apart. Starts on the smallest ring so the first members match the ring
  // layout a smaller fan would have used.
  let r = MIN_RADIUS_PX + 2;
  let a = -Math.PI / 2;
  const growthPerRad = SPACING_PX / (2 * Math.PI);
  for (let i = 0; i < n; i++) {
    out.push([r * Math.cos(a), r * Math.sin(a)]);
    const step = SPACING_PX / r;
    a += step;
    r += growthPerRad * step;
  }
  return out;
}

/** Fan out every set of markers that would stack on screen at `zoom`.
 *
 *  Returns one result per input, in input order. A marker with no neighbour
 *  inside STACK_PX comes back untouched (no `from`), so a map with nothing
 *  stacked is byte-identical to one that never called this. */
export function spread(items: readonly SpreadInput[], zoom: number,
                       stackPx = STACK_PX): SpreadResult[] {
  const world = worldPx(zoom);
  const px = items.map((it) => toPx(it.lon, it.lat, world));

  // Grid-hash at the stacking distance, then check the 3×3 neighbourhood: a
  // marker joins the first cluster whose SEED is within reach, else seeds its
  // own. Seed-anchored (not chained) on purpose — a chain of markers each one
  // stack-width apart is a line of distinct dots, not one pile.
  const cellOf = (x: number, y: number): [number, number] =>
    [Math.floor(x / stackPx), Math.floor(y / stackPx)];
  const seeds = new Map<string, number[]>();   // cell -> cluster ids seeded there
  const members: number[][] = [];
  const seedPx: [number, number][] = [];
  for (let i = 0; i < items.length; i++) {
    const [x, y] = px[i]!;
    const [cx, cy] = cellOf(x, y);
    let found = -1;
    outer: for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const ids = seeds.get(`${cx + dx}:${cy + dy}`);
        if (!ids) continue;
        for (const c of ids) {
          const [sx, sy] = seedPx[c]!;
          if (Math.hypot(sx - x, sy - y) < stackPx) { found = c; break outer; }
        }
      }
    }
    if (found < 0) {
      found = members.length;
      members.push([]);
      seedPx.push([x, y]);
      const key = `${cx}:${cy}`;
      const list = seeds.get(key);
      if (list) list.push(found); else seeds.set(key, [found]);
    }
    members[found]!.push(i);
  }

  const out: SpreadResult[] = items.map((it) => ({ lon: it.lon, lat: it.lat }));
  for (const idx of members) {
    if (idx.length < 2) continue;
    // The fan centres on the members' mean — for an identical-point stack that
    // IS the point; for a near-identical one it is the middle of the pile.
    let mx = 0; let my = 0;
    for (const i of idx) { mx += px[i]![0]; my += px[i]![1]; }
    mx /= idx.length; my /= idx.length;
    const sorted = [...idx].sort((a, b) =>
      (items[a]!.order ?? 0) - (items[b]!.order ?? 0) || a - b);
    const offsets = fanOffsets(sorted.length);
    for (let k = 0; k < sorted.length; k++) {
      const i = sorted[k]!;
      const [ox, oy] = offsets[k]!;
      const [lon, lat] = fromPx(mx + ox, my + oy, world);
      const it = items[i]!;
      out[i] = { lon, lat, from: [it.lon, it.lat] };
    }
  }
  return out;
}

function fromPx(x: number, y: number, world: number): [number, number] {
  const lon = (x / world) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * y) / world;
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return [lon, lat];
}

/** Screen distance between two points at a zoom — the test's ruler, and the
 *  bake's "did that actually separate them" check. */
export function pixelDistance(a: [number, number], b: [number, number],
                              zoom: number): number {
  const world = worldPx(zoom);
  const [ax, ay] = toPx(a[0], a[1], world);
  const [bx, by] = toPx(b[0], b[1], world);
  return Math.hypot(ax - bx, ay - by);
}
